import { useEffect, useState } from "react";
import { Video, RotateCcw, ShieldCheck, X, Circle, Square } from "lucide-react";
import { useVideoRecorder, MAX_SECONDS } from "@/lib/useVideoRecorder";

export default function VideoVerification({ code, value, onCapture, onClear, onReady, disabled }) {
  const { videoRef, phase, error, setError, countdown, elapsed, start, beginCountdown, stopRecording } = useVideoRecorder(onCapture);
  const [previewUrl, setPreviewUrl] = useState("");
  const [playbackError, setPlaybackError] = useState("");
  useEffect(() => {
    if (!value) { setPreviewUrl(""); return; }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const retake = () => {
    setPlaybackError("");
    setError("");
    onClear();
  };
  const recording = phase === "recording";
  const starting = phase === "starting";
  const showCam = !value && !error && phase !== "idle";
  const progressPct = Math.min(100, (elapsed / MAX_SECONDS) * 100);

  return (
    <div data-testid="video-verification" className="space-y-4">
      <div className="flex items-center justify-between rounded-2xl bg-white/[0.04] border border-white/15 px-4 py-3">
        <div className="flex items-center gap-3">
          <ShieldCheck size={16} strokeWidth={1.6} className="text-white/60" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">
              Say this code out loud
            </p>
            <p data-testid="video-challenge-code" className="font-display text-2xl md:text-3xl font-light tabular-nums tracking-widest text-white">
              {code}
            </p>
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 text-right hidden sm:block">
          {MAX_SECONDS}s max
        </p>
      </div>

      <div className="relative aspect-square rounded-3xl overflow-hidden bg-black border border-white/10">
        {value ? (
          <video
            key="recorded-playback"
            src={previewUrl || undefined}
            preload="auto"
            onLoadedData={() => onReady(true)}
            onError={() => { onReady(false); setPlaybackError("This recording could not be played. Please retake it before submitting."); }}
            controls
            playsInline
            className="w-full h-full object-cover"
            data-testid="video-preview"
          />
        ) : (
          <>
            <video
              key="live-camera"
              data-testid="video-live-camera"
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover scale-x-[-1]"
            />
            {showCam && !recording && countdown === 0 && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-[70%] h-[80%] rounded-[50%] border border-white/40 border-dashed" />
              </div>
            )}
            {countdown > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span className="font-display text-9xl font-light text-white" data-testid="video-countdown">
                  {countdown}
                </span>
              </div>
            )}
            {recording && (
              <>
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-red-500/90 text-white px-3 py-1.5 rounded-full">
                  <Circle size={8} fill="currentColor" className="animate-pulse" />
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold">Recording</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                  <div
                    className="h-full bg-white"
                    style={{ width: `${progressPct}%`, transition: "width 100ms linear" }}
                  />
                </div>
              </>
            )}
            {phase === "idle" && (
              <div data-testid="video-camera-placeholder" className="absolute inset-0 flex items-center justify-center p-8 text-center text-sm text-white/60">
                Start your camera when you're ready. Keep your face in frame and say the four digits clearly.
              </div>
            )}
            {(starting || phase === "processing") && !recording && (
              <div className="absolute inset-0 flex items-center justify-center text-white/60 text-xs uppercase tracking-[0.25em] bg-black/60">
                {starting ? "Starting camera…" : "Preparing your recording…"}
              </div>
            )}
          </>
        )}
      </div>

      {(error || playbackError) && (
        <div data-testid="video-error" role="alert" className="rounded-2xl border border-red-400/40 bg-red-500/10 text-red-200 text-xs px-4 py-3 flex items-start gap-2">
          <X size={14} className="mt-0.5 shrink-0" />
          <span>{error || playbackError}</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        {value ? (
          <button
            type="button"
            data-testid="video-retake-btn"
            onClick={retake}
            disabled={disabled}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white text-xs uppercase tracking-[0.25em] font-bold px-5 py-3 active:scale-[0.98] transition-transform"
          >
            <RotateCcw size={13} /> Retake
          </button>
        ) : recording ? (
          <button
            type="button"
            data-testid="video-stop-btn"
            onClick={stopRecording}
            disabled={elapsed < 2}
            className="inline-flex items-center gap-2 rounded-full bg-red-500 text-white text-xs uppercase tracking-[0.25em] font-bold px-6 py-3 active:scale-[0.98] transition-transform"
          >
            <Square size={12} fill="currentColor" /> Stop
          </button>
        ) : (
          <button
            type="button"
            data-testid={phase === "idle" ? "video-start-camera-btn" : "video-record-btn"}
            onClick={phase === "idle" ? start : beginCountdown}
            disabled={starting || countdown > 0 || phase === "processing"}
            className="inline-flex items-center gap-2 rounded-full bg-white text-black text-xs uppercase tracking-[0.25em] font-bold px-6 py-3 active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            <Video size={14} /> {phase === "idle" ? (error ? "Try camera again" : "Start camera") : "Record a video"}
          </button>
        )}
      </div>

      <p data-testid="video-privacy-notice" className="text-xs text-white/60 leading-relaxed">
        {value ? "Play your clip and check your face and voice before submitting. " : "Camera and microphone are used only for this short clip. "}
        Your recording stays on this device until you submit. It is then stored privately for the scouting team to compare with your photos. This is a manual review, not an automated identity or liveness check.
      </p>
    </div>
  );
}
